import { ChangeDetectorRef, Component, ViewChild } from '@angular/core';
import { Subscription } from 'rxjs';
import { DrawerInputConfig } from '../../../models/drawer-model';
import { RouteService } from '../../../service/route.service';
import { API_ENDPOINTS } from '../../shared/constants/apiEnpoints';
import { ADMIN_API_SUCCESS_CODE, MAX_INT_32, PRACTICE_LOAD_STATUS_CODES } from '../../shared/constants/values';
import { DrawerComponent } from '../../shared/drawer/drawer.component';
import { DynamicGridComponent } from '../../shared/dynamic-grid/dynamic-grid.component';
import { GridConfig } from '../../shared/dynamic-grid/grid-model/grid-config.model';
import { Toast, ToastComponent } from '../../shared/toast/toast.component';
import { HttpClient } from '@angular/common/http';
import { NIL as NIL_UUID } from 'uuid';
import { ActivatedRoute } from '@angular/router';

export interface PracticeConnectionstringData {
  id: string;
  existingDatabaseServerName: string;
  existingDatabaseName: string;
  newDatabaseServerName: string;
  newDatabaseName: string;
  connectionString?: string;
}
export interface PracticeMasterData {
  // id: string;
  pmsName: string;
  name: string;
  sequenceId: string;
  locationId: string;
  licenseKey: string;
  statusName: string;
  isInstalled: boolean;
  pmsVersion: string;
  isActive: boolean;
  isLive: boolean;
  pmsStatusCode: number;
  pmsId: string;
  timezone: string;
  transformStatus: boolean;
}
@Component({
  selector: 'app-practice-master',
  templateUrl: './practice-master.component.html',
  styleUrl: './practice-master.component.scss',
})
export class PracticeMasterComponent {
  @ViewChild('practiceDrawer') drawerComponent!: DrawerComponent<PracticeMasterData>;
  @ViewChild('connectionstringDrawer') editConnectionstringDrawerComponent!: DrawerComponent<PracticeConnectionstringData>;

  @ViewChild(DynamicGridComponent) dynamicGrid!: DynamicGridComponent;

  // Method to disable error highlighting in the grid
  disableErrorHighlight = () => false;

  // Settings properties
  hideAuditColumns: boolean = JSON.parse(localStorage.getItem('hideAuditColumns')) || false;
  isSettingsDropdownOpen: boolean = false;

  //check if any query param exists
  isAnyQueryParamExists: boolean = Object.keys(this.route.snapshot.queryParams).length > 0;

  isSuperAdmin: boolean = localStorage.getItem('userRole')?.toUpperCase() === 'SUPER ADMIN';

  // Delete confirmation
  showDeleteConfirmation: boolean = false;
  practiceToDelete: any = null;

  gridConfig: GridConfig = {
    columns: [
      {
        dataType: 'toggle',
        jsonKey: 'isActive',
        label: 'Active',
        isSortable: false,
      },
      {
        dataType: 'toggle',
        jsonKey: 'isLive',
        label: 'Live',
        isSortable: false,
      },
      {
        dataType: 'toggle',
        jsonKey: 'transformStatus',
        label: 'Ready For Base',
        isSortable: false,
      },
      { dataType: 'string', jsonKey: 'practiceCode', label: 'Practice Code', isSticky: true },
      { dataType: 'string', jsonKey: 'name', label: 'Practice Name', width: 'full', },
      { dataType: 'string', jsonKey: 'pmsName', label: 'PMS Name' },
      {
        dataType: 'string',
        jsonKey: 'pmsVersion',
        label: 'Supported PMS Version',
      },
      {
        dataType: 'string',
        jsonKey: 'latestPMSVersion',
        label: 'Latest PMS Version',
      },
      {
        dataType: 'string',
        jsonKey: 'pmsSourceDatabaseVersion',
        label: 'PMS SOURCE DATABASE VERSION',
      },
      {
        dataType: 'boolean',
        jsonKey: 'isHyperv',
        label: 'Hyper-V',
      },
      {
        dataType: 'string',
        jsonKey: 'licenseKey',
        label: 'License Key',
        isSortable: false,
      },
      { dataType: 'boolean', jsonKey: 'isInstalled', label: 'Installed' },
      {
        dataType: 'date',
        jsonKey: 'installationDate',
        label: 'Installation Date',
      },
      {
        dataType: 'string',
        jsonKey: 'pmsAgentVersion',
        label: 'PMS Agent Version',
      },
      {
        dataType: 'string',
        jsonKey: 'internalPracticeId',
        label: 'Internal Practice Id',
      },
      { dataType: 'string', jsonKey: 'sequenceId', label: 'Sequence Id' },
      { dataType: 'string', jsonKey: 'locationId', label: 'Location Id' },
      {
        dataType: 'string',
        jsonKey: 'timezone',
        label: 'Time Zone',
        width: 'full',
      },
      {
        dataType: 'number',
        jsonKey: 'lastReportedDownloadSpeed',
        label: 'LAST REPORTED DOWNLOAD SPEED (mbps)',
        noUppercase: true,
      },
      {
        dataType: 'number',
        jsonKey: 'lastReportedUploadSpeed',
        label: 'LAST REPORTED UPLOAD SPEED (mbps)',
        noUppercase: true,
      },
      {
        dataType: 'string',
        jsonKey: 'computerName',
        label: 'COMPUTER NAME',
        width: 'full',
      },
      {
        label: 'OS Description',
        jsonKey: 'os',
        dataType: 'string',
        width: 'full',
      },
      {
        label: 'Free Disk Space %',
        jsonKey: 'freeDiskSpacePercentage',
        dataType: 'number',
      },
      {
        label: 'Processor Name',
        jsonKey: 'processorName',
        dataType: 'string',
        width: 'full',
      },
      {
        label: 'Logical Processor Count',
        jsonKey: 'processorCount',
        dataType: 'number',
      },
      {
        label: 'Total Available Physical Memory (GB)',
        jsonKey: 'totalAvailableMemoryGBytes',
        dataType: 'number',
      },
      {
        label: 'Private App Memory (GB)',
        jsonKey: 'privateMemoryGBytes',
        dataType: 'number',
        isSortable: false,
      },
      {
        dataType: 'string',
        jsonKey: 'pmsInstallationFolder',
        label: 'PMS INSTALLATION FOLDER',
        width: 'full',
      },
      {
        dataType: 'string',
        jsonKey: 'machineKey',
        label: 'Machine ID',
        isSortable: false,
      },
      {
        label: 'Last Load Date',
        jsonKey: 'lastLoadDate',
        dataType: 'date',
      },
      {
        label: 'Last Load Status',
        jsonKey: 'lastLoadStatus',
        dataType: 'string',
        width: 'full',
      },
      {
        label: 'Created By',
        jsonKey: 'createdByUserEmail',
        dataType: 'string',
        hideOnToggle: true,
      },
      {
        label: 'Created At',
        jsonKey: 'createdAt',
        dataType: 'date',
        hideOnToggle: true,
      },
      {
        label: 'Modified By',
        jsonKey: 'modifiedByUserEmail',
        dataType: 'string',
        hideOnToggle: true,
      },
      {
        label: 'Modified At',
        jsonKey: 'modifiedAt',
        dataType: 'date',
        hideOnToggle: true,
      },
      {
        label: 'Notes',
        jsonKey: 'notes',
        dataType: 'string',
        width: 'full',
        isSortable: false,
      }
    ],
    filters: [
      {
        label: 'PMS Name',
        jsonKey: 'pmsId',
        type: 'multi',
        options: [],
        width: 'w-50',
      },
      {
        label: 'Status',
        jsonKey: 'pmsLastLoadStatus',
        type: 'multi',
        options: [],
        width: 'w-50',
      },
    ],
    deletable: this.isSuperAdmin,
    editable: true,
    editable2: true,
    disableEdit2Condition: { pmsName: ['Dentrix'] },
    exportable: true,
    displayAdd: true,
    isToggleDisabled: true,
    defaultFilters: !this.isAnyQueryParamExists ?  { isActive: true } : {},
    uniqueTableName: 'PracticeMaster',
    displayHideAuditColumnsToggle: true,
  };

  getDataUrl = API_ENDPOINTS.PRACTICES;
  deleteDataUrl = API_ENDPOINTS.PRACTICE_DELETE;
  addDataUrl = API_ENDPOINTS.PRACTICES;
  add2DataUrl = API_ENDPOINTS.PRACTICE_UPDATE_CONNECTIONSTRING;
  editDataUrl = API_ENDPOINTS.PRACTICE_BY_ID;
  edit2DataUrl = API_ENDPOINTS.PRACTICE_UPDATE_CONNECTIONSTRING;
  toggleUrl = API_ENDPOINTS.TOGGLE_PRACTICE;
  resetUrl = API_ENDPOINTS.PRACTICE_RESET_INSTALLATION;
  publishTopic = API_ENDPOINTS.PUBLISH_TOPIC;
  getPracticesUrl = API_ENDPOINTS.PMS_MASTER;
  getStatusUrl = API_ENDPOINTS.PMS_STATUS;

  connectionstringDrawerInputConfig: DrawerInputConfig[] = [
    {
      section: 'Server Name and Database Name',
      dataType: 'string',
      jsonKey: 'existingDatabaseServerName',
      label: 'Existing Database Instance Name',
      input: 'text',
      isDisabled: true,
      minLength: 1,
      maxLength: 500,
    },
    {
      section: 'Server Name and Database Name',
      dataType: 'string',
      jsonKey: 'existingDatabaseName',
      label: 'Existing Database Name',
      input: 'text',
      isDisabled: true,
      minLength: 1,
      maxLength: 500,
    },
    {
      section: 'Server Name and Database Name',
      dataType: 'string',
      jsonKey: 'newDatabaseServerName',
      label: 'New Database Instance Name',
      input: 'text',
      isRequired: true,
      minLength: 1,
      maxLength: 500,
    },
    {
      section: 'Server Name and Database Name',
      dataType: 'string',
      jsonKey: 'newDatabaseName',
      label: 'New Database Name',
      input: 'text',
      isRequired: true,
      minLength: 1,
      maxLength: 500,
    },
    {
      section: 'Connection String',
      dataType: 'string',
      jsonKey: 'connectionString',
      label: 'Connection String',
      input: 'textarea',
      isRequired: true,
      minLength: 1,
      maxLength: 500,
    }
  ];

  drawerInputConfig: DrawerInputConfig[] = [
    {
      dataType: 'string',
      jsonKey: 'practiceCode',
      input: 'text',
      label: 'Practice Code',
      isRequired: true,
      minLength: 1,
      maxLength: 50,
      validationRegex: /^[A-Z0-9]+$/,
    },
    {
      dataType: 'string',
      jsonKey: 'name',
      label: 'Practice Name',
      input: 'text',
      isRequired: true,
      minLength: 1,
      maxLength: 500,
    },
    {
      dataType: 'string',
      jsonKey: 'sequenceId',
      label: 'Sequence Id',
      input: 'text',
      isRequired: true,
      minLength: 1,
      maxLength: 500,
      isDisabled: true,
    },
    {
      dataType: 'string',
      jsonKey: 'locationId',
      label: 'Location ID',
      input: 'text',
      isRequired: true,
      minLength: 1,
      maxLength: 500,
    },
    {
      dataType: 'string',
      jsonKey: 'pmsName',
      label: 'PMS Name',
      input: 'select',
      dropDownOptionsURL: API_ENDPOINTS.PMS_MASTER,
      dropdownJsonKey: 'pmsNameVersion',
      isRequired: true,
      dropdownSortDirection: 'asc',
      foreignKeyProperty: 'pmsId',
    },
    {
      dataType: 'string',
      jsonKey: 'timezone',
      label: 'Time Zone',
      input: 'select',
      isRequired: false,
      dropdownSortDirection: 'asc',
      dropdownJsonKey: 'text',
      isDropDownSearchable: true,
      dropDownOptionsURL: API_ENDPOINTS.TIMEZONE,
      uniqueIdentifier: 'PracticeTimezone',
    },
    {
      dataType: 'string',
      jsonKey: 'licenseKey',
      label: 'License Key',
      input: 'text',
      isRequired: true,
      minLength: 7,
      maxLength: 7,
      validationRegex: /^\d+$/,
      isAutoGenerated: true,
      isDisabled: true,
    },
    {
      dataType: 'string',
      jsonKey: 'os',
      label: 'Os Description',
      input: 'text',
      isDisabled: true,
      minLength: 1,
      maxLength: 500,
    },
    {
      dataType: 'string',
      jsonKey: 'pmsSourceDatabaseVersion',
      label: 'PMS SOURCE DATABASE VERSION',
      input: 'text',
      isDisabled: true,
      minLength: 1,
      maxLength: 1000,
    },
    {
      dataType: 'string',
      jsonKey: 'totalAvailableMemoryGBytes',
      label: 'Total Available Physical Memory (GB)',
      input: 'text',
      isDisabled: true,
      minLength: 1,
      maxLength: 500,
    },
    {
      dataType: 'string',
      jsonKey: 'privateMemoryGBytes',
      label: 'Private App Memory (GB)',
      input: 'text',
      isDisabled: true,
      minLength: 1,
      maxLength: 500,
    },
    {
      dataType: 'string',
      jsonKey: 'processorName',
      label: 'Processor Name',
      input: 'text',
      isDisabled: true,
      minLength: 1,
      maxLength: 500,
    },
    {
      dataType: 'string',
      jsonKey: 'processorCount',
      label: 'Logical Processor Count',
      input: 'text',
      isDisabled: true,
      minLength: 1,
      maxLength: 500,
    },
    {
      dataType: 'string',
      label: 'Computer Name',
      jsonKey: 'computerName',
      input: 'text',
      isRequired: false,
      isDisabled: true,
      isHideOnAddDrawer: true,
    },
    {
      dataType: 'string',
      label: 'PMS Installation Folder',
      jsonKey: 'pmsInstallationFolder',
      input: 'text',
      isRequired: false,
      isDisabled: true,
      isHideOnAddDrawer: true,
    },
    {
      label: 'Notes',
      jsonKey: 'notes',
      input: 'textarea',
      dataType: 'string',
      isRequired: false,
      minLength: 1,
    },
    {
      label: 'Enable PMS Schema Integrity Check',
      jsonKey: 'enablePmsSchemaIntegrityCheck',
      input: 'toggle',
      dataType: 'boolean',
    },
    {
      input: 'toggle-group',
      label: 'Status Settings',
      jsonKey: 'statusGroup',
      dataType: 'custom',
      toggles: [
        { jsonKey: 'isActive', label: 'Active' },
        { jsonKey: 'isLive', label: 'Live' },
        { jsonKey: 'transformStatus', label: 'Ready For Base' }
      ],
    },
    {
      input: 'custom-table',
      label: 'Practice Operational Hours',
      jsonKey: 'practiceOperationalHours',
      dataType: 'custom',
    }
  ];

  drawerModel: PracticeMasterData = {
    sequenceId: '',
    locationId: '',
    isActive: true,
    isLive: false,
    isInstalled: false,
    pmsVersion: '',
    licenseKey: '',
    name: '',
    pmsName: 'Select PMS',
    statusName: 'Select Status',
    pmsStatusCode: ADMIN_API_SUCCESS_CODE,
    pmsId: '',
    timezone: '',
    transformStatus: null,
  };

  connectionstringDrawerModel: PracticeConnectionstringData = {
    id: NIL_UUID,
    existingDatabaseServerName: '',
    existingDatabaseName: '',
    newDatabaseServerName: '',
    newDatabaseName: '',
    connectionString: null,
  };

  private _routerSubscription: Subscription;
  public get routerSubscription(): Subscription {
    return this._routerSubscription;
  }
  public set routerSubscription(value: Subscription) {
    this._routerSubscription = value;
  }
  routes: string[] = [];
  constructor(
    private routeService: RouteService,
    private cdr: ChangeDetectorRef,
    private _http: HttpClient,
    private route: ActivatedRoute
  ) { }

  ngOnInit() {
    this.fetchFilterOptions();
  }

  ngAfterViewInit(): void {
    this.drawerComponent.drawerModelName = 'practiceMasterData',
      this._routerSubscription = this.routeService
        .getRoutes()
        .subscribe((data) => {
          this.routes = data;
          this.routes.shift();
          if (this.routes.length == 2) {
            this.routeService.redirectTO(
              this.routes.join('/').concat('/practices')
            );
          }
          this.cdr.detectChanges();
        });
  }

  fetchFilterOptions() {
    this.fetchPMSNameOptions();
    this.fetchPMSStatusOptions();
  }

  fetchPMSNameOptions() {
    this._http
      .get<any>(this.getPracticesUrl, { params: { pageSize: MAX_INT_32 } })
      .subscribe((response) => {
        response.data = response.data
          .filter((option) => option.isActive)
          .map((option) => ({
            ...option,
            name: `${option.name} ${option.version}`,
          }))
          .sort((a, b) => a.name.localeCompare(b.name));
        response.data = [{ id: 'All', name: 'All PMS', checked: true }, ...response.data];

        this.gridConfig.filters.find(
          (filter) => filter.jsonKey === 'pmsId'
        ).options = response;
      });
  }

  fetchPMSStatusOptions() {
    this._http
      .get<any>(this.getStatusUrl)
      .subscribe((response) => {
        response.data = response.data
          .map((option) => ({
            ...option,
            name: option.source + ' - ' + option.name,
          }))
          .filter((record) => PRACTICE_LOAD_STATUS_CODES.includes(record.id))
          .sort((a, b) => {
            const getName = (item) => item.name.toLowerCase();
            const aName = getName(a);
            const bName = getName(b);

            if (aName.includes('progress')) return -1;
            if (bName.includes('progress')) return 1;
            if (aName.includes('complete')) return -1;
            if (bName.includes('complete')) return 1;
            return aName.localeCompare(bName);
          });
        response.data = [{ id: 'All', name: 'All Status', checked: true }, ...response.data];
        this.gridConfig.filters.find(
          (filter) => filter.jsonKey === 'pmsLastLoadStatus'
        ).options = response;
      });
  }

  private readonly PMS_SEQUENCE_ID_MAP: Record<string, string> = {
    'Abeldent': '30',
    'Tracker': '40',
    'Power Practice': '50',
    'Dentrix': '60',
    'Cleardent': '70',
  };

  onPmsNameChange(model: PracticeMasterData): void {
    if (this.drawerComponent?.drawerType !== 'add') return;
    const pmsName = model.pmsName;
    const match = Object.keys(this.PMS_SEQUENCE_ID_MAP).find(key =>
      pmsName?.toLowerCase().includes(key.toLowerCase())
    );
    if (match) {
      this.drawerComponent.drawerModel = { ...model, sequenceId: this.PMS_SEQUENCE_ID_MAP[match] };
    }
  }

  onAddHandler(): void {
    if (this.drawerComponent) {
      this.drawerComponent.drawerType = 'add';
      this.drawerInputConfig[0].isDisabled = false;
      this.resetEntry();
      this.drawerComponent.openDrawer();
    } else {
      console.error('Drawer component is not initialized');
    }
  }

  resetEntry() {
    this.drawerModel = {
      sequenceId: '',
      locationId: '',
      isActive: true,
      isLive: false,
      isInstalled: false,
      pmsVersion: '',
      licenseKey: '',
      name: '',
      pmsName: 'Select PMS',
      statusName: 'Select Status',
      pmsStatusCode: ADMIN_API_SUCCESS_CODE,
      pmsId: '',
      timezone: '',
      transformStatus: false,
    };

    this.connectionstringDrawerModel = {
      id: '',
      existingDatabaseServerName: '',
      existingDatabaseName: '',
      newDatabaseServerName: '',
      newDatabaseName: '',
    };
  }

  onEditHandler(rowData: PracticeMasterData): void {
    rowData.pmsName = rowData.pmsName + ' ' + rowData.pmsVersion;
    if (this.drawerComponent) {
      this.drawerComponent.drawerType = 'edit';
      this.drawerInputConfig[0].isDisabled = true;
      this.drawerComponent.drawerModel = rowData;
      this.drawerComponent.openDrawer();
    } else {
      console.error('Drawer component is not initialized');
    }
  }

  onEdit2Handler(rowData: PracticeConnectionstringData): void {
    this.connectionstringDrawerModel.id = rowData.id;
    this.connectionstringDrawerModel.existingDatabaseServerName = rowData.existingDatabaseServerName || null;
    this.connectionstringDrawerModel.existingDatabaseName = rowData.existingDatabaseName || null;
    this.connectionstringDrawerModel.connectionString = rowData.connectionString || null;

    if (this.editConnectionstringDrawerComponent) {
      this.editConnectionstringDrawerComponent.drawerModelName = 'practiceMasterData';
      this.editConnectionstringDrawerComponent.drawerType = 'edit';
      this.editConnectionstringDrawerComponent.drawerModel = this.connectionstringDrawerModel;
      this.editConnectionstringDrawerComponent.openDrawer();
    } else {
      console.error('Drawer component is not initialized');
    }
  }

  refreshHandler() {
    this.dynamicGrid.fetchData();
  }

  onDeleteHandler(rowData: any): void {
    this.practiceToDelete = rowData;
    this.showDeleteConfirmation = true;
  }

  onDeleteConfirmation(action: 'confirm' | 'cancel'): void {
    if (action === 'confirm' && this.practiceToDelete) {
      const practiceCode = this.practiceToDelete.practiceCode;
      const url = this.deleteDataUrl.replace(':id', this.practiceToDelete.id) + `?practiceCode=${practiceCode}`;
      this._http.delete(url).subscribe({
        next: () => {
          this.toastComponent.showToast({ severity: 'success', title: 'Success', message: `Practice ${practiceCode} deleted successfully.` });
          this.refreshHandler();
        },
        error: (err) => {
          this.toastComponent.showToast({ severity: 'error', title: 'Error', message: err?.error?.errorMessage || 'Failed to delete practice.' });
        }
      });
    }
    this.showDeleteConfirmation = false;
    this.practiceToDelete = null;
  }

  // toast related properties

  @ViewChild(ToastComponent) toastComponent!: ToastComponent;

  showToast(toast: Toast) {
    this.toastComponent.showToast(toast);
    this.refreshHandler();
  }

  ngOnDestroy() {
    this._routerSubscription.unsubscribe();
  }
}
